import { axiosInstance } from './index';

// Helper to normalize list responses that may be wrapped
const normalizeList = (data, key) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data[key])) return data[key];
  if (data && Array.isArray(data.data)) return data.data;
  return [];
};

// Map backend combo shape → frontend offer card shape
const mapComboFromApi = (c) => {
  if (!c) return null;

  const id = c.id;
  const name = c.offerName || c.offer_name || c.name || '';
  const description = c.description || '';

  const comboPrice =
    c.comboPrice != null
      ? c.comboPrice
      : c.combo_price != null
      ? c.combo_price
      : c.price;

  const originalPrice =
    c.originalPrice != null
      ? c.originalPrice
      : c.original_price != null
      ? c.original_price
      : c.individualPrice != null
      ? c.individualPrice
      : null;

  let rawProducts = [];
  if (typeof c.products === 'string') {
    try {
      rawProducts = JSON.parse(c.products);
    } catch {
      rawProducts = [];
    }
  } else if (Array.isArray(c.products)) {
    rawProducts = c.products;
  } else if (Array.isArray(c.productDetails)) {
    rawProducts = c.productDetails;
  }

  // Map backend product shape { product_id, product_name, quantity, ... } → { name, weight, unit }
  const products = rawProducts.map((p) => ({
    name: p.product_name ?? p.name ?? '',
    weight: p.quantity ?? p.weight ?? '',
    unit: p.unit ?? (p.quantity != null ? ' qty' : ''),
    productId: p.product_id ?? p.productId,
  }));

  const items =
    c.items != null
      ? c.items
      : c.itemCount != null
      ? c.itemCount
      : products.length;

  const isActive =
    c.isActive != null
      ? Boolean(c.isActive)
      : c.is_active != null
      ? Boolean(c.is_active)
      : true;

  const priceNum = Number(comboPrice);
  const origNum = originalPrice != null ? Number(originalPrice) : null;

  return {
    id,
    name: name || '',
    description: description || '',
    price: Number.isFinite(priceNum) ? priceNum : 0,
    originalPrice: origNum != null && Number.isFinite(origNum) ? origNum : null,
    products: Array.isArray(products) ? products : [],
    items: typeof items === 'number' && items >= 0 ? items : (Array.isArray(products) ? products.length : 0),
    isActive,
  };
};

// ────────────────────────────────────────────────────────────────────────────────
// Combo APIs
// ────────────────────────────────────────────────────────────────────────────────

export const fetchCombos = async () => {
  const response = await axiosInstance.get('/combos');
  const list = normalizeList(response.data, 'combos');
  return list.map(mapComboFromApi).filter(Boolean);
};

export const fetchComboById = async (id) => {
  const response = await axiosInstance.get(`/combos/${id}`);
  return mapComboFromApi(response.data?.combo || response.data);
};

export const fetchComboDetails = async (id) => {
  const response = await axiosInstance.get(`/combos/${id}/details`);
  return response.data;
};

export const fetchComboSavings = async (id) => {
  const response = await axiosInstance.get(`/combos/${id}/savings`);
  return response.data;
};

// Create/update helpers
const buildComboFormData = (combo, imageFile) => {
  const formData = new FormData();

  formData.append('offerName', combo.name);
  if (combo.description) formData.append('description', combo.description);
  formData.append('comboPrice', combo.price);
  if (combo.originalPrice != null && combo.originalPrice !== '') {
    formData.append('originalPrice', combo.originalPrice);
  }
  formData.append('products', JSON.stringify(combo.products || []));

  if (imageFile) {
    formData.append('comboImage', imageFile);
  }

  return formData;
};

export const createCombo = async (combo, imageFile) => {
  const formData = buildComboFormData(combo, imageFile);
  const response = await axiosInstance.post('/combos', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const created =
    response.data?.combo ||
    response.data?.data ||
    response.data;
  const mapped = created ? mapComboFromApi(created) : null;
  // Merge with submitted data so card shows correct details even if API returns minimal response
  const submittedPrice = Number(combo.price);
  const submittedItems = combo.products?.length ?? combo.items ?? 0;
  return {
    ...(mapped || {}),
    id: mapped?.id ?? created?.id,
    name: (mapped?.name || combo.name) || '',
    description: mapped?.description ?? combo.description ?? '',
    price: Number.isFinite(mapped?.price) && mapped.price > 0 ? mapped.price : (Number.isFinite(submittedPrice) ? submittedPrice : 0),
    originalPrice: (mapped?.originalPrice != null && Number.isFinite(mapped.originalPrice)) ? mapped.originalPrice : (combo.originalPrice != null ? Number(combo.originalPrice) : null),
    products: (mapped?.products?.length ? mapped.products : combo.products) || [],
    items: (mapped?.items != null && mapped.items > 0) ? mapped.items : submittedItems,
  };
};

export const updateCombo = async (id, combo, imageFile) => {
  const formData = buildComboFormData(combo, imageFile);
  const response = await axiosInstance.put(`/combos/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const updated =
    response.data?.combo ||
    response.data?.data ||
    response.data;
  const mapped = mapComboFromApi(updated);
  return {
    ...mapped,
    id: mapped?.id ?? id,
    name: mapped?.name || combo.name,
    description: mapped?.description ?? combo.description,
    price: Number.isFinite(mapped?.price) ? mapped.price : Number(combo.price) || 0,
    originalPrice: mapped?.originalPrice != null ? mapped.originalPrice : combo.originalPrice,
    products: (mapped?.products?.length ? mapped.products : combo.products) || [],
    items: (mapped?.items != null && mapped.items > 0) ? mapped.items : (combo.products?.length ?? combo.items ?? 0),
  };
};

export const deleteCombo = async (id) => {
  await axiosInstance.delete(`/combos/${id}`);
};

export const toggleComboStatus = async (id, isActive) => {
  const response = await axiosInstance.patch(`/combos/${id}/toggle-status`, {
    isActive,
  });
  const updated =
    response.data?.combo ||
    response.data?.data ||
    response.data;
  return mapComboFromApi(updated);
};

export { mapComboFromApi };

